/**
 * calculatedFieldUtils - helper(s) for “calculated” virtual fields
 * ------------------------------------------------------------------
 *  • A *calculated* field is stored in fieldsMetadata with:
 *      {
 *        type   : 'calculated',
 *        label  : 'Submitted data',
 *        formula: 'CONCAT',             // <string> formula keyword
 *        fields : ['firstName','lastName'], // <array> optional – UFIs to use
 *        delimiter: ' ',                // <string> optional – CONCAT joiner
 *      }
 *
 *  • During submit-time we receive:
 *      – meta   → the metadata blob above
 *      – byUfi → an object { [ufi]: answerValue }
 *
 *  • We return a plain JS value (string | number | …) that gets
 *    pushed into submissionData like any other field.
 *
 *   This file has **zero React/Firebase imports** so it’s
 *     safe to call from both client and Cloud Functions later on.
 * ------------------------------------------------------------------ */
import { FIELD_TYPE_CALCULATED } from '../components/Fields/constants/fieldTypes.js';
//import { replaceChipsWithValues } from './formUtils.js';   // ← new

/* -------------------------------------------------------- helpers */
/**
 * Safely coerce anything to a string for concat.
 */
const safeToString = (v) =>
  v === null || v === undefined ? '' : Array.isArray(v) ? v.join(', ') : String(v);

/* ---------------------------------------------------- main export */
/**
 * Evaluate a single calculated-field definition.
 *
 * @param {Object} meta   – the field’s metadata (type MUST be 'calculated')
 * @param {Object} byUfi – map of { ufi → submittedAnswer }
 * @returns {*}          – the computed value (string, number, etc.)
 */
export function evaluateCalculatedField(meta, byUfi = {}) {
  if (!meta || meta.type !== FIELD_TYPE_CALCULATED) {
    console.warn('[calculatedFieldUtils] Called with non-calculated meta:', meta);
    return '';
  }

  // normalise
  const formula    = (meta.formula || 'CONCAT').toUpperCase();
  const srcFields  = Array.isArray(meta.fields) && meta.fields.length
      ? meta.fields
      : Object.keys(byUfi);               // default: all answers
  const delimiter  = meta.delimiter ?? ' ';

  /* --------------- switch on formula keyword --------------- */
  switch (formula) {
    /* ---------- NEW  branch – full-sentence template ---------- */
case 'TEMPLATE': {
  const tpl = meta.template || '';
  return tpl.replace(/{{\s*([^}]+?)\s*}}/g, (_, ufiRaw) => {
    const ufi = String(ufiRaw || '').trim();
    const val = byUfi[ufi];
    return val === undefined || val === null ? '' : String(val);
  });
}



    /* ---------------- concat strings ---------------- */
    case 'CONCAT': {
      return srcFields
        .map((ufi) => safeToString(byUfi[ufi]))
        .filter((s) => s !== '')            // skip empties
        .join(delimiter)
        .trim();
    }

    /* ---------------- numeric add ------------------ */
    case 'ADD': {
      return srcFields.reduce(
        (sum, ufi) => sum + Number(byUfi[ufi] ?? 0), 0,
      );
    }

    /* -------------- numeric subtract --------------- */
    case 'SUB': {
      if (srcFields.length === 0) return 0;
      const [first, ...rest] = srcFields;
      return rest.reduce(
        (acc, ufi) => acc - Number(byUfi[ufi] ?? 0),
        Number(byUfi[first] ?? 0),
      );
    }

    /* -------------- simple date format ------------- */
    /*  meta.dateFormat e.g. 'MM/DD/YYYY' (future) */
    case 'FORMAT_DATE': {
      const ufi = srcFields[0];
      const raw = byUfi[ufi];
      if (!raw) return '';
      // Expect raw ISO or Date; you can extend later
      try {
        const d = new Date(raw);
        return isNaN(d) ? '' : d.toLocaleDateString();
      } catch {
        return '';
      }
    }

    /* ----------------- default branch --------------- */
    default: {
      console.warn(
        `[calculatedFieldUtils] Unknown formula "${meta.formula}". Falling back to CONCAT.`,
      );
      return srcFields
        .map((ufi) => safeToString(byUfi[ufi]))
        .filter((s) => s !== '')
        .join(delimiter)
        .trim();
    }
  }
}

/* Convenience – evaluate *all* calculated metas */
export function evaluateAllCalculatedFields(allMetas = {}, byUfi = {}) {
  const results = {};
  Object.values(allMetas).forEach((meta) => {
    if (meta.type === FIELD_TYPE_CALCULATED) {
      results[meta.ufi] = evaluateCalculatedField(meta, byUfi);
    }
  });
  return results;
}
